import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { switchMap } from 'rxjs/operators';

import {
  Customer,
  CustomerRequest,
  CustomerUpdateStatusRequest,
  CustomerUpdateLogoRequest,
  CustomerNameList
} from './customer.model';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private apiUrl = `${environment.apiURL}`;

  constructor(private http: HttpClient) { }

  get baseApiUrl(): string {
    return this.apiUrl;
  }


  // Get all customers by client ID
  getCustomersByClientId(clientId: string): Observable<Customer[]> {
    return this.http.get<Customer[]>(`${this.apiUrl}/customer/list/${clientId}`);
  }

  // Get customer by ID
  getCustomerById(customerId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/customer/get/${customerId}`);
  }
  // Create new customer
  createCustomer(customerRequest: CustomerRequest): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/customer/add`, customerRequest);
  }

  // Update existing customer
  updateCustomer(customerId: string, customerRequest: CustomerRequest): Observable<string> {
    return this.http.put<string>(`${this.apiUrl}/customer/edit/${customerId}`, customerRequest);
  }

  // Check if customer is associated with locations
  checkAssociatedWithLocations(customerId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/customer/checkAssociatedwithLocations/${customerId}`);
  }

  // Delete customer
  deleteCustomer(customerId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/customer/delete/${customerId}`);
  }

  // Update customer status (Active/Inactive)
  updateCustomerStatus(customerId: string, statusRequest: CustomerUpdateStatusRequest): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/customer/status/${customerId}`, statusRequest);
  }

  // Update customer logo
  updateCustomerLogo(customerId: string, logoRequest: CustomerUpdateLogoRequest): Observable<string> {
    return this.http.patch<string>(`${this.apiUrl}/customer/logo/${customerId}`, logoRequest);
  }

  getImageAsBase64(url: string): Observable<string> {
    return this.http.get(url, { headers: new HttpHeaders({ 'X-Skip-Auth': 'true' }), responseType: 'blob' }).pipe(
      switchMap(blob => this.convertBlobToBase64(blob))
    );
  }

  private convertBlobToBase64(blob: Blob): Observable<string> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.readAsDataURL(blob);
    });
  }

  // Get all customers for dropdown
  getCustomers(clientId: string): Observable<CustomerNameList[]> {
    return this.http.get<CustomerNameList[]>(`${this.apiUrl}/customer/nameList/${clientId}`);
  }

  getCustomersListByClientId(clientId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/customer/getCustomersByClientId/${clientId}`);
  }

  getCustomersListByClientIds(clientIds: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${this.apiUrl}/customer/customers`, clientIds);
  }

  // Get customers for current logged-in user based on their role and permissions
  // No clientId parameter required - automatically determines accessible customers
  
  getMyCustomers(isNorrel?: boolean): Observable<Customer[]> {
    let params: any = {};

    if (isNorrel !== undefined) {
      params.isNorrel = isNorrel.toString();
    }

    return this.http.get<Customer[]>(`${this.apiUrl}/customer/mycustomers`, { params });
  }
}